import React, { useContext, useState, useEffect } from 'react';
import { navigate } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  maxBreakpointQuery,
  standardColours,
  greyColours,
  brandColours,
  fontSize,
  labelStyles,
  selectWrapStyles,
  selectStyles,
  inputStyles,
  visuallyHidden,
  standardTransition,
} from '../styles';
import { Svg, Button, Link } from './ui';
import SpaSearchCalendar from './SpaSearchCalendar';
import { StoreContext } from '../context/StoreContext';
import {
  useLocations,
  getLocation,
  formatDate,
  buildUrl,
  onClickDataLayer,
} from '../utils';
import targetIcon from '../images/target-icon.svg';
import calendarIcon from '../images/calendar-icon.bg.svg';
import searchIcon from '../images/search-icon.bg.svg';

const StyledSpaSearch = styled.section`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${({ isHomepage }) => {
    if (isHomepage) {
      return css`
        margin: auto;
        max-width: 580px;
      `;
    } else {
      return css`
        margin: 30px 0;

        ${minBreakpointQuery.mlarge`
          margin-top: 40px;
          margin-bottom: 40px;
        `}
      `;
    }
  }}

  ${({ isSpaDaysPage, isSpaBreaksPage, isSpaHotelsPage }) => {
    if (isSpaDaysPage || isSpaBreaksPage || isSpaHotelsPage) {
      return css`
        ${minBreakpointQuery.large`
          margin-bottom: 50px;
        `}

        ${minBreakpointQuery.xlarge`
          margin-bottom: 60px;
        `}

        ${minBreakpointQuery.xxlarge`
          margin-bottom: 70px;
        `}
      `;
    }
  }}
`;

const StyledForm = styled.form`
  width: 100%;
  background-color: ${standardColours.white};
  border-radius: 10px;

  ${maxBreakpointQuery.small`
    position: relative;
  `}

  ${({ searchFields, isBanner, isBestsellersSearch }) => {
    if (
      (searchFields ? searchFields.arrivalDate : true) &&
      !isBanner &&
      !isBestsellersSearch
    ) {
      return css`
        ${minBreakpointQuery.tiny`
          display: grid;
          grid-template-columns: 1fr 1.7fr;
          align-items: center;
        `}
      `;
    }
  }}
  
  ${({ isBanner, isHomepage }) => {
    if (isBanner && !isHomepage) {
      return css`
        ${minBreakpointQuery.tsmall`
          display: grid;
          grid-template-columns: 1fr 1.7fr;
          align-items: center;
        `}
      `;
    } else {
      return css`
        border: 1px solid ${greyColours.lightGrey};

        ${minBreakpointQuery.small`
          position: relative;
        `}
      `;
    }
  }}
  
  ${({ searchFields, isBestsellersSearch }) => {
    if (
      (searchFields ? searchFields.arrivalDate : true) &&
      !isBestsellersSearch
    ) {
      return css`
        ${minBreakpointQuery.small`
          grid-template-columns: repeat(2, 1fr);
        `}
      `;
    }
  }}

  ${({ isAffiliatePage, isBestsellersSearch }) => {
    if (!isAffiliatePage && !isBestsellersSearch) {
      return css`
        ${minBreakpointQuery.mlarge`
          grid-template-columns: 2fr 3fr 2.2fr 1fr;
        `}
      `;
    }
  }}

  ${({ searchFields }) => {
    if (searchFields && searchFields.arrivalDate === false) {
      return css`
        ${minBreakpointQuery.small`
          display: grid;
          grid-template-columns: 2fr 3fr 1fr;
        `}
      `;
    }
  }}

  ${({ isBanner }) => {
    if (isBanner) {
      return css`
        ${minBreakpointQuery.mlarge`
          grid-template-columns: 1fr 1.8fr 1.2fr 1.4fr;
        `}
      `;
    }
  }}
`;

const commonFieldStyles = (isBanner, isHomepage, fieldWrapper) => {
  if (isBanner && isHomepage && fieldWrapper === 'type') {
    return css`
      display: block;
      padding: 12px 15px 0;
    `;
  } else if (isBanner && isHomepage && fieldWrapper === 'date') {
    return css`
      display: block;
      padding: 12px 0 12px 15px;
    `;
  } else if (isBanner && isHomepage) {
    return css`
      display: block;
      padding: 12px 15px;
    `;
  } else if (isBanner) {
    return css`
      display: block;
      padding: 14px 15px;

      ${minBreakpointQuery.small`
        padding: 20px;
      `}
    `;
  } else {
    return css`
      display: block;
      padding: 11px 15px;

      ${minBreakpointQuery.small`
        padding-right: 20px;
        padding-left: 20px;
      `}
    `;
  }
};

const StyledTypeFieldWrapper = styled.div`
  ${({ isBanner, isHomepage }) =>
    commonFieldStyles(isBanner, isHomepage, 'type')}

  ${({ searchFields, isBanner, isBestsellersSearch }) => {
    if (
      (searchFields ? searchFields.arrivalDate : true) &&
      !isBanner &&
      !isBestsellersSearch
    ) {
      return css`
        ${minBreakpointQuery.tiny`
          border-right: 1px solid ${greyColours.lightGrey};
        `}
      `;
    }
  }}
  
  ${({ isBanner, isHomepage }) => {
    if (isBanner && !isHomepage) {
      return css`
        ${maxBreakpointQuery.tsmall`
          padding-bottom: 0;
        `}

        ${minBreakpointQuery.tsmall`
          padding-right: 0;
        `}
      `;
    }
  }}
  
  ${({ searchFields, isHomepage, isBestsellersSearch }) => {
    if (
      (searchFields ? searchFields.arrivalDate : true) &&
      !isHomepage &&
      !isBestsellersSearch
    ) {
      return css`
        ${minBreakpointQuery.small`
          border-bottom: 1px solid ${greyColours.lightGrey};
        `}
      `;
    }
  }}

  ${({ searchFields, isAffiliatePage }) => {
    if ((searchFields ? searchFields.arrivalDate : true) && !isAffiliatePage) {
      return css`
        ${minBreakpointQuery.mlarge`
          border-bottom: none;
        `}
      `;
    }
  }}

  ${({ searchFields }) => {
    if (searchFields && searchFields.arrivalDate === false) {
      return css`
        ${maxBreakpointQuery.small`
          border-bottom: 1px solid ${greyColours.lightGrey};
        `}

        ${minBreakpointQuery.small`
          border-right: 1px solid ${greyColours.lightGrey};
        `}
      `;
    }
  }}
`;

const StyledTypeFieldRadio = styled.fieldset``;

const StyledTypeFieldSelect = styled.label``;

const StyledDestinationFieldWrapper = styled.div`
  ${({ isBanner, isHomepage }) => commonFieldStyles(isBanner, isHomepage)}

  ${minBreakpointQuery.mlarge`
    position: relative;
  `}

  ${({ searchFields, isBanner, isBestsellersSearch }) => {
    if (
      (searchFields ? searchFields.arrivalDate : true) &&
      !isBanner &&
      !isBestsellersSearch
    ) {
      return css`
        ${maxBreakpointQuery.tiny`
          border-top: 1px solid ${greyColours.lightGrey};
        `}
      `;
    }
  }}

  ${({ searchFields, isHomepage, isBestsellersSearch }) => {
    if (
      (searchFields ? searchFields.arrivalDate : true) &&
      !isHomepage &&
      !isBestsellersSearch
    ) {
      return css`
        ${minBreakpointQuery.small`
          border-bottom: 1px solid ${greyColours.lightGrey};
        `}
      `;
    }
  }}

  ${({ searchFields }) => {
    if (searchFields && searchFields.arrivalDate === false) {
      return css`
        ${maxBreakpointQuery.small`
          padding-right: 95px;
          border-right: none;
        `}
      `;
    }
  }}

  ${({ isBestsellersSearch }) => {
    if (isBestsellersSearch) {
      return css`
        padding-right: 95px;
        border-top: 1px solid ${greyColours.lightGrey};
        border-right: none;

        ${minBreakpointQuery.small`
          padding-right: 100px;
        `}
      `;
    }
  }}

  ${({ isHomepage, isAffiliatePage, isBestsellersSearch }) => {
    if (!isHomepage && !isAffiliatePage && !isBestsellersSearch) {
      return css`
        ${minBreakpointQuery.mlarge`
          border-right: 1px solid ${greyColours.lightGrey};
          border-bottom: none;
        `}
      `;
    }
  }}
`;

const StyledDestinationField = styled.label``;

const StyledDateFieldWrapper = styled.div`
  ${({ isBanner, isHomepage }) =>
    commonFieldStyles(isBanner, isHomepage, 'date')}
  position: relative;

  ${({ isHomepage }) => {
    if (isHomepage) {
      return css`
        padding-right: 95px;
        border-top: 1px solid ${greyColours.lightGrey};

        ${minBreakpointQuery.small`
          padding-right: 140px;
        `}
      `;
    } else {
      return css`
        ${maxBreakpointQuery.small`
          grid-column: span 2;
          padding-right: 95px;
          border-top: 1px solid ${greyColours.lightGrey};
        `}

        ${minBreakpointQuery.small`
          border-right: 1px solid ${greyColours.lightGrey};
        `}
      `;
    }
  }}
`;

const StyledDateField = styled.label``;

const StyledLabel = styled.span`
  ${labelStyles()};
  margin-bottom: 0;
  padding: 0 2px;
  ${fontSize(13)};
  text-transform: uppercase;
`;

const StyledTypeRadioHeading = styled.legend`
  ${visuallyHidden()};
`;

const StyledTypeRadio = styled.div`
  display: flex;
  height: 51px;
  border: 1px solid ${greyColours.lightGrey};
  border-radius: 10px;
`;

const StyledTypeRadioOption = styled.label`
  flex-grow: 1;
  flex-basis: 0;

  span {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    height: 100%;
    color: ${greyColours.darkPurplishGrey};
    ${fontSize(13)};
    line-height: 1.1;
    text-align: center;
    border-radius: 5px;
    cursor: pointer;
    transition: ${standardTransition('color-color')},
      ${standardTransition('background-color')};

    ${({ isHomepage }) => {
      if (isHomepage) {
        return css`
          ${fontSize(14)};

          ${minBreakpointQuery.small`
            ${fontSize(15)};
          `}

          ${minBreakpointQuery.large`
            ${fontSize(16)};
          `}
        `;
      }
    }}
  }

  input {
    ${visuallyHidden()};

    &:checked {
      + span {
        color: ${standardColours.white};
        background-color: ${brandColours.primary};
      }
    }
  }
`;

const StyledTypeSelectWrap = styled.span`
  ${selectWrapStyles()};

  &:before,
  &:after {
    right: 4px;
  }
`;

const StyledTypeSelect = styled.select`
  ${selectStyles()};
  padding: 2px 16px 2px 2px;
  height: 30px;
  border: none;

  &:focus {
    outline: 0;
    border-bottom: 1px solid ${greyColours.darkPurplishGrey};
  }
`;

const commonInputStyles = () => {
  return css`
    ${inputStyles()};
    padding: 2px;
    height: 30px;
    border: none;

    &:focus {
      outline: 0;
      border-bottom: 1px solid ${greyColours.darkPurplishGrey};
    }
  `;
};

const StyledDestinationLocationFinder = styled.span`
  position: relative;
  display: block;
  padding-right: 30px;
`;

const StyledDestination = styled.input`
  ${commonInputStyles()};
`;

const StyledLocationFinder = styled.button`
  position: absolute;
  top: 50%;
  right: 0;
  padding: 6px;
  transform: translateY(-50%);

  svg {
    display: block;
    height: 18px;
    width: 18px;
    fill: ${greyColours.darkPurplishGrey};

    ${({ findingLocation }) => {
      if (findingLocation) {
        return css`
          animation: pulse-black 2s infinite;
        `;
      }
    }}
  }

  @keyframes pulse-black {
    0% {
      transform: scale(1);
      fill: ${greyColours.darkPurplishGrey};
    }

    50% {
      transform: scale(1.2);
      fill: ${brandColours.tertiary};
    }

    100% {
      transform: scale(1);
      fill: ${greyColours.darkPurplishGrey};
    }
  }

  span {
    ${visuallyHidden()};
  }
`;

const StyledLocations = styled.div`
  position: absolute;
  left: 0;
  z-index: 1;
  margin-top: 8px;
  padding: 10px 15px 0;
  width: 100%;
  max-height: 240px;
  overflow: auto;
  background-color: ${standardColours.white};
  border-radius: 10px;
  box-shadow: 0 1px 12px 0 ${standardColours.transparentBlack(0.4)};

  ${minBreakpointQuery.tiny`
    margin-top: 12px;
  `}

  ${minBreakpointQuery.small`
    padding-right: 20px;
    padding-left: 20px;
  `}

  ${minBreakpointQuery.mlarge`
    width: 500px;
  `}

  ${({ isBanner }) => {
    if (isBanner) {
      return css`
        ${minBreakpointQuery.small`
          margin-top: 16px;
        `}

        ${minBreakpointQuery.mlarge`
          margin-top: 24px;
        `}
      `;
    }
  }}
`;

const StyledLocationsGroup = styled.div`
  padding: 8px 0;
  border-top: 1px solid ${greyColours.lightGrey};

  &:first-child {
    border-top: 0;
  }
`;

const StyledLocationsType = styled.p`
  color: ${greyColours.darkPurplishGrey};
  ${fontSize(14)};
  text-transform: capitalize;
`;

const StyledLocationsList = styled.ul`
  margin: 6px 0;
`;

const StyledLocationsItem = styled.li`
  margin: 6px 0;
`;

const StyledLocationsButton = styled.button`
  padding: 0;
  line-height: 1.2;
  text-align: left;
`;

const StyledDate = styled.input`
  ${commonInputStyles()};
  background: url(${calendarIcon}) no-repeat calc(100% - 2px) 50% / 16px 16px;
  opacity: 1;
  pointer-events: none;
`;

const StyledButton = styled(Button)`
  position: absolute;
  right: 0;
  bottom: 0;
  height: 80px;
  width: 80px;
  background-image: url(${searchIcon});
  background-repeat: no-repeat;
  background-position: 50% 50%;
  background-size: 22px 22px;
  border-bottom-right-radius: 10px;

  ${({ searchFields }) => {
    if (searchFields && searchFields.arrivalDate === false) {
      return css`
        ${minBreakpointQuery.small`
          border-top-right-radius: 10px;
        `}
      `;
    }
  }}

  ${({ isBanner, isHomepage, isAffiliatePage }) => {
    if (isBanner && !isHomepage) {
      return css`
        ${minBreakpointQuery.small`
          margin: 20px;
          min-width: 200px;
        `}
      `;
    } else if (isAffiliatePage) {
      return css`
        height: 69px;
      `;
    } else if (!isHomepage) {
      return css`
        height: 69px;

        ${minBreakpointQuery.small`
          margin: 0;
          height: 69px;
          border-bottom-right-radius: 10px;
        `}

        ${minBreakpointQuery.mlarge`
          border-top-right-radius: 10px;
        `}
      `;
    }
  }}
  
  ${({ isHomepage }) => {
    if (isHomepage) {
      return css`
        height: 72px;

        ${minBreakpointQuery.small`
          width: 125px;
          background-image: none;
        `};
      `;
    }
  }}
  
  ${({ isHomepage, isBestsellersSearch }) => {
    if (!isHomepage && !isBestsellersSearch) {
      return css`
        ${minBreakpointQuery.small`
          position: static;
          width: auto;
          background-image: none;
        `}
      `;
    }
  }}

  ${({ isBestsellersSearch }) => {
    if (isBestsellersSearch) {
      return css`
        right: 1px;
        bottom: 1px;

        ${minBreakpointQuery.mlarge`
          border-top-right-radius: 0;
        `}
      `;
    }
  }}

  span {
    ${({ isBestsellersSearch }) => {
      if (!isBestsellersSearch) {
        return css`
          ${maxBreakpointQuery.small`
            ${visuallyHidden()};
          `}
        `;
      } else {
        return css`
          ${visuallyHidden()};
        `;
      }
    }}
  }
`;

const StyledLink = styled(Link)`
  margin-top: 15px;
  text-transform: uppercase;

  ${minBreakpointQuery.large`
    margin-top: 20px;
  `}

  &:after {
    content: '➔';
    margin-left: 12px;
  }

  ${({ isBanner }) => {
    if (isBanner) {
      return css`
        color: ${standardColours.white};
      `;
    }
  }}
`;

const SpaSearch = ({
  searchFields,
  searchType: initialSearchType,
  location: initialLocation,
  arrivalDate: initialArrivalDate,
  baseUrl,
  isBanner,
  isHomepage,
  isSpaDaysPage,
  isSpaBreaksPage,
  isSpaHotelsPage,
  isAffiliatePage,
  isGenericSpaListingPage,
  isBestsellersSearch,
  ...props
}) => {
  const { recentlyViewed, addLocationHighRatingSpasToRecentlyViewed } =
    useContext(StoreContext);

  const [searchType, setSearchType] = useState(initialSearchType);
  const [destination, setDestination] = useState();
  const [locationTerm, setLocationTerm] = useState();
  const [location, setLocation] = useState();
  const [showLocations, setShowLocations] = useState(false);
  const [findingLocation, setFindingLocation] = useState(false);
  const [selectedDate, setSelectedDate] = useState({
    date: initialArrivalDate,
  });
  const [showCalendar, setShowCalendar] = useState(false);

  const stayTypes = [
    {
      name: 'View All',
      value: 'All',
    },
    {
      name: 'Spa Breaks',
      value: 'Breaks',
    },
    {
      name: 'Spa Days',
      value: 'Days',
    },
  ];

  if (!isGenericSpaListingPage) {
    stayTypes.push({
      name: 'Spa Deals',
      value: 'Deals',
    });
  }

  const locations = useLocations(locationTerm, searchType);

  const handleTypeChange = value => {
    setSearchType(value);

    if (location) {
      // If we have a location already then try and use this to search for the new ID
      setLocationTerm(destination);
    }
  };

  const handleDestinationChange = value => {
    setDestination(value);

    if (value.length > 2) {
      setLocationTerm(value);
      setShowLocations(true);
    } else {
      setShowLocations(false);
    }
  };

  const handleDestinationBlur = value => {
    if (destination && locations?.locations?.length) {
      const loc = locations.locations.find(l => l.displayName === destination);
      if (loc) {
        changeLocation(loc);
      } else {
        changeLocation(locations.locations[0]);
      }
    }
  };

  const changeLocation = location => {
    setLocation(location);
    setDestination(location.displayName || location.name);
    setShowLocations(false);
  };

  useEffect(() => {
    if (initialLocation) {
      changeLocation(initialLocation);
    }
  }, [initialLocation]);

  useEffect(() => {
    if (location) {
      // find location in new locations using destination, check groups, locations and spas arrays
      if (locations && locations.groups) {
        locations.groups.find(group => {
          group.name === destination && setLocation(group);
        });
      }

      if (locations && locations.locations) {
        locations.locations.find(location => {
          location.name === destination && setLocation(location);
        });
      }

      if (locations && locations.spas) {
        locations.spas.find(spa => {
          spa.name === destination && setLocation(spa);
        });
      }
    }
  }, [locations]);

  const getCurrentLocation = async () => {
    if (navigator.geolocation) {
      setFindingLocation(true);
      navigator.geolocation.getCurrentPosition(
        async ({ coords: { latitude, longitude } }) => {
          const location = await getLocation(latitude, longitude, searchType);
          setFindingLocation(false);
          changeLocation(location);
        },
        error => {
          setFindingLocation(false);
          alert(`ERROR(${error.code}): ${error.message}`);
        },
      );
    }
  };

  const handleSubmit = e => {
    e.preventDefault();

    let url =
      searchType === 'Breaks'
        ? 'spa-breaks/all'
        : searchType === 'Days'
          ? 'spa-days/all'
          : searchType === 'Deals'
            ? 'spa-deals'
            : 'spas';

    if (location) {
      url = location.url;

      if (!recentlyViewed.length) {
        addLocationHighRatingSpasToRecentlyViewed(
          location.id || location.locationId,
        );
      }
    } else if (destination && locations?.locations?.length > 0) {
      url = locations.locations[0].url;
    }

    // Send dataLayer
    const dataLayer = {
      customData: {
        event: 'spa_search',
        type_of_stay: searchType ? searchType : 'All',
        destination: destination,
      },
    };

    onClickDataLayer(dataLayer);

    navigate(buildUrl(isGenericSpaListingPage ? baseUrl : url), {
      state: {
        searchType: searchType,
        locationId: location && (location.id || location.locationId),
        arrivalDate: selectedDate && selectedDate.date,
        shouldUpdateScroll: !isGenericSpaListingPage,
      },
    });
  };

  const navigateToLocation = location => {
    // Send dataLayer
    const dataLayer = {
      customData: {
        event: 'spa_search',
        type_of_stay: searchType ? searchType : 'All',
        destination: location.name,
      },
    };

    onClickDataLayer(dataLayer);

    navigate(buildUrl(location.url), {
      state: { arrivalDate: selectedDate && selectedDate.date },
    });
  };

  return (
    <StyledSpaSearch
      isHomepage={isHomepage}
      isSpaDaysPage={isSpaDaysPage}
      isSpaBreaksPage={isSpaBreaksPage}
      isSpaHotelsPage={isSpaHotelsPage}
      {...props}
    >
      <StyledForm
        id="spa-search"
        autoComplete="off"
        searchFields={searchFields}
        isBanner={isBanner}
        isHomepage={isHomepage}
        isAffiliatePage={isAffiliatePage}
        isBestsellersSearch={isBestsellersSearch}
        onSubmit={e => {
          handleSubmit(e);
        }}
      >
        <StyledTypeFieldWrapper
          searchFields={searchFields}
          isBanner={isBanner}
          isHomepage={isHomepage}
          isAffiliatePage={isAffiliatePage}
          isBestsellersSearch={isBestsellersSearch}
        >
          {isBanner ? (
            <StyledTypeFieldRadio>
              <StyledTypeRadioHeading>Type of Stay</StyledTypeRadioHeading>
              <StyledTypeRadio>
                {stayTypes.map(({ value, name }, i) => (
                  <StyledTypeRadioOption key={i} isHomepage={isHomepage}>
                    <input
                      type="radio"
                      name="stay-type"
                      value={value}
                      defaultChecked={value === initialSearchType || i === 0}
                      onChange={e => handleTypeChange(e.currentTarget.value)}
                    />
                    <span>{name}</span>
                  </StyledTypeRadioOption>
                ))}
              </StyledTypeRadio>
            </StyledTypeFieldRadio>
          ) : (
            <StyledTypeFieldSelect>
              <StyledLabel>Type of Stay</StyledLabel>
              <StyledTypeSelectWrap>
                <StyledTypeSelect
                  name="stay-type"
                  required
                  onChange={e => handleTypeChange(e.currentTarget.value)}
                >
                  {stayTypes.map(({ value, name }, id) => (
                    <option
                      key={id}
                      value={value}
                      selected={value === initialSearchType}
                    >
                      {name}
                    </option>
                  ))}
                </StyledTypeSelect>
              </StyledTypeSelectWrap>
            </StyledTypeFieldSelect>
          )}
        </StyledTypeFieldWrapper>
        <StyledDestinationFieldWrapper
          searchFields={searchFields}
          isBanner={isBanner}
          isHomepage={isHomepage}
          isAffiliatePage={isAffiliatePage}
          isBestsellersSearch={isBestsellersSearch}
        >
          <StyledDestinationField>
            <StyledLabel>Destination</StyledLabel>
            <StyledDestinationLocationFinder>
              <StyledDestination
                type="text"
                name="destination"
                placeholder="Postcode, Spa Name or Town"
                value={destination}
                onChange={e => handleDestinationChange(e.currentTarget.value)}
                onBlur={e => handleDestinationBlur(e.currentTarget.value)}
              />
              <StyledLocationFinder
                type="button"
                findingLocation={findingLocation}
                onClick={() => getCurrentLocation()}
              >
                <Svg image={targetIcon} />
                <span>Find my location</span>
              </StyledLocationFinder>
            </StyledDestinationLocationFinder>
          </StyledDestinationField>
          {showLocations &&
            locations &&
            (locations.locations.length > 0 || locations.spas.length > 0) && (
              <StyledLocations
                isBanner={isBanner}
                onMouseDown={e => e.preventDefault()}
              >
                {Object.entries(locations).map(
                  ([name, items]) =>
                    items.length > 0 && (
                      <StyledLocationsGroup>
                        <StyledLocationsType>{name}</StyledLocationsType>
                        <StyledLocationsList>
                          {items.map(item => (
                            <StyledLocationsItem key={item.locationId}>
                              <StyledLocationsButton
                                type="button"
                                onClick={() => {
                                  if (
                                    (item.spaId || item.spaGroupId) &&
                                    item.url
                                  ) {
                                    navigateToLocation(item);
                                  } else {
                                    changeLocation(item);
                                  }
                                }}
                              >
                                {name === 'locations'
                                  ? item.displayName || item.name
                                  : item.name}
                              </StyledLocationsButton>
                            </StyledLocationsItem>
                          ))}
                        </StyledLocationsList>
                      </StyledLocationsGroup>
                    ),
                )}
              </StyledLocations>
            )}
        </StyledDestinationFieldWrapper>
        {(searchFields ? searchFields.arrivalDate : true) &&
          !isBestsellersSearch && (
            <StyledDateFieldWrapper isBanner={isBanner} isHomepage={isHomepage}>
              <StyledDateField>
                <div onClick={() => setShowCalendar(true)}>
                  <StyledLabel>Arrival Date</StyledLabel>
                  <StyledDate
                    type="text"
                    name="arrival-date"
                    value={formatDate(selectedDate && selectedDate.date)}
                    placeholder="Any date"
                    disabled
                  />
                </div>
                {showCalendar && (
                  <SpaSearchCalendar
                    searchType={searchType}
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    setShow={setShowCalendar}
                  />
                )}
              </StyledDateField>
            </StyledDateFieldWrapper>
          )}
        <StyledButton
          alt={true}
          searchFields={searchFields}
          isBanner={isBanner}
          isHomepage={isHomepage}
          isAffiliatePage={isAffiliatePage}
          isBestsellersSearch={isBestsellersSearch}
        >
          Search
        </StyledButton>
      </StyledForm>
      {(isHomepage || isAffiliatePage) && (
        <StyledLink to="spas" isBanner={isBanner}>
          View all Spas
        </StyledLink>
      )}
    </StyledSpaSearch>
  );
};

export default SpaSearch;
